import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX
} from 'react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../types';
import { onRoleButtonKeyDown, toReactProps } from '../utils';
import { Flex, ScreenReaderOnly } from '../';
import titleStyles from '../title/title.module.css';
import styles from './progress.module.css';
import clsx from 'clsx';
import '@u-elements/u-progress';

type Size = 'sm' | 'lg';
type Value = string | number;
type Variants =
  | 'bar'
  | 'dot'
  | 'step-vertical'
  | 'step-horizontal'
  | 'discount';

export type ProgressProps<
  Variant extends Variants = 'bar',
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
> = PolymorphicComponentPropWithRef<
  As,
  { 'aria-label': string; value?: Value } & (
    | {
        variant?: 'bar';
        size?: never;
        max: Value;
        justify?: never;
        steps?: never;
        labels?: never;
      }
    | {
        variant: 'dot';
        size?: Size;
        max?: Value;
        justify?: never;
        steps?: never;
        labels?: never;
      }
    | {
        variant: 'step-vertical';
        size?: Size;
        max?: never;
        justify?: never;
        steps?: never;
        labels?: never;
      }
    | {
        variant: 'step-horizontal';
        size?: Size;
        max?: never;
        justify?: 'center' | 'space-between';
        steps?: never;
        labels?: never;
      }
    | {
        variant: 'discount';
        size?: never;
        max?: Value;
        justify?: never;
        steps?: number | number[];
        labels?: string[];
      }
  )
>;

const ProgressComp = <
  Variant extends Variants,
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
>(
  {
    children,
    className,
    justify,
    labels,
    max,
    size,
    steps,
    value,
    variant = 'bar',
    ...rest
  }: ProgressProps<Variant, As>,
  ref: PolymorphicRef<As>
) => {
  const intMax = Number(max) || Number(value) || 0;
  const intValue = Math.max(0, Math.min(Number(value) || 0, intMax)); // Ensure value is minimum 0 and maximum intMax
  const innerRef = useRef<As>(null);
  const isStep = variant.startsWith('step-');
  const role = isStep ? 'list' : 'group';
  const [isInitialized, setIsInitialized] = useState(false);
  const classes = clsx(
    styles[variant],
    styles[size || 'lg'],
    !justify || styles[justify],
    className
  );

  useImperativeHandle(ref, () => innerRef.current as As); // Forward innerRef

  // useEffect only for 'step-' variants
  useEffect(() => {
    const self = innerRef.current as HTMLElement | null;
    const steps = self?.querySelectorAll<HTMLElement>(`.${styles.step}`);
    if (!steps?.length) return; // No need run rest of code if no steps present

    const titles = self?.querySelectorAll<HTMLElement>(
      `.${styles.step} .${titleStyles.title}:first-child`
    );
    titles?.forEach((title) => (title.role = 'presentation')); // Make title not announce as part of step

    const animationDelay =
      Number(
        getComputedStyle(steps[0])
          .getPropertyValue('--step-animation-delay') // var only exists if no-preference on motion
          .trim()
      ) || 0;

    /** Setup steps **/
    steps?.forEach((step, index) => {
      const desc = step.querySelector(`.${styles.description}`)?.textContent;

      if (!desc && !step.ariaLabel)
        console.warn(self, `must have aria-label or children.`);

      step.ariaCurrent = `${index === intValue && 'step'}`; // Set state in client with vanilla JS for performance
      step.classList.toggle(styles.isNext, index > intValue);
      step.classList.toggle(styles.isCurrent, index === Math.floor(intValue));
      step.classList.toggle(styles.isPrev, index < intValue);
      step.style.animationDelay = `${index * animationDelay}ms`; // Will be 0 if user prefers reduced motion
    });

    /** Setup progress between steps **/
    const decimal = intValue - Math.floor(intValue);
    const stepA = steps?.[Math.floor(intValue)];
    const stepB = steps?.[Math.ceil(intValue)];
    let resizeObserver: ResizeObserver;

    // Only delay calculation of progress-between line on mount (when animations run),
    // and no delay needed if user prefers reduced motion
    if (!isInitialized) {
      if (!animationDelay) setIsInitialized(true);
      else {
        const calcDelay = Math.ceil(intValue + 1) * (animationDelay + 50); // 50ms buffer
        const timeout = setTimeout(() => {
          setIsInitialized(true);
        }, calcDelay);

        return () => clearTimeout(timeout);
      }
    }

    // ResizeObserver is not supported in Vitest
    if (
      decimal &&
      stepA &&
      stepB &&
      isInitialized &&
      'ResizeObserver' in window
    ) {
      resizeObserver = new ResizeObserver(() => {
        const rectA = getRect(stepA?.querySelector(`.${styles.dot}`));
        const rectB = getRect(stepB?.querySelector(`.${styles.dot}`));

        stepA?.style.setProperty(
          '--progress-between',
          `${Math.round((variant === 'step-vertical' ? rectB.top - rectA.bottom : rectB.left - rectA.right) * decimal)}px`
        );
      });
      resizeObserver.observe(stepA);
      resizeObserver.observe(stepB);

      return () => {
        titles?.forEach((title) => title.removeAttribute('role'));
        stepA?.style.removeProperty('--progress-between');
        resizeObserver?.disconnect();
      };
    }
  }, [intValue, variant, isInitialized]);

  return variant === 'bar' ? (
    <u-progress
      class={classes}
      max={intMax}
      ref={innerRef}
      value={intValue} // Limit to maximum intMax
      {...rest}
    />
  ) : variant === 'discount' ? (
    <div className={classes} ref={innerRef} aria-live="polite" {...rest}>
      <u-progress
        class={styles.bar}
        max={intMax || 100}
        value={intValue}
        aria-hidden="true"
      />

      {steps && typeof steps === 'number'
        ? Array.from({ length: Math.max(0, steps - 1) }).map((_, i) => (
            <span
              aria-hidden="true"
              key={i}
              className={styles.divider}
              style={
                {
                  '--divider-position': Math.round((100 / steps) * (i + 1))
                } as React.CSSProperties
              }
            />
          ))
        : Array.isArray(steps) &&
          steps.slice(0, -1).map((step, i) => (
            <span
              aria-hidden="true"
              key={i}
              className={styles.divider}
              style={
                {
                  '--divider-position': (step / (intMax || 100)) * 100
                } as React.CSSProperties
              }
            />
          ))}

      {labels && (
        <Flex aria-hidden="true" gap="none" wrap="nowrap">
          {Array.isArray(labels) &&
            labels.map((label, i) => {
              return (
                <Flex
                  key={i}
                  as="span"
                  justify="end"
                  className={styles.label}
                  style={
                    {
                      '--label-position': (100 / labels.length).toFixed(2)
                    } as React.CSSProperties
                  }
                >
                  {label}
                </Flex>
              );
            })}
        </Flex>
      )}
    </div>
  ) : (
    <div className={classes} role={role} ref={innerRef} {...rest}>
      {children || (!isStep && `${intValue}/${intMax}`)}
    </div>
  );
};

export type ProgressStepProps = React.ComponentPropsWithoutRef<'div'> & {
  dot?: React.ReactNode | React.ComponentPropsWithoutRef<'div'>;
};

const Step = (
  {
    ['aria-label']: ariaLabel,
    className,
    children,
    dot,
    ...rest
  }: ProgressStepProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const isClickable = !!rest.onClick || undefined; // Fallback to undefined so React does not render attributes unless true
  const dotProps = toReactProps<React.ComponentPropsWithoutRef<'div'>>(dot);
  const { children: dotChildren, ...dotRest } = dotProps;

  return (
    <div
      {...rest}
      role="listitem"
      className={clsx(styles.step, className)}
      ref={ref}
    >
      <div
        {...dotRest}
        className={clsx(styles.dot, dotRest.className)}
        aria-hidden="true"
      >
        {dotChildren}
      </div>
      {!!ariaLabel && <ScreenReaderOnly>{ariaLabel}</ScreenReaderOnly>}
      {/* Always render description element, so we can ensure we have role button and focus outline when clickable */}
      <div
        className={styles.description}
        onKeyDown={isClickable && onRoleButtonKeyDown}
        role={isClickable && 'button'}
        tabIndex={isClickable && 0}
      >
        {children}
      </div>
    </div>
  );
};

// TS struggles with this polymorphic component (perhaps because we return the variants like we do),
// only way to make it happy is saying ref is HTMLProgressElement
const ProgressComponent = forwardRef<HTMLProgressElement, ProgressProps>(
  ProgressComp
) as <
  Variant extends Variants = 'bar',
  As extends React.ElementType = Variant extends 'bar' ? 'progress' : 'div'
>(
  props: ProgressProps<Variant, As>
) => JSX.Element;

const StepComp = forwardRef<HTMLDivElement, ProgressStepProps>(Step);

export const Progress = Object.assign(ProgressComponent, { Step: StepComp });
(ProgressComponent as React.FC).displayName = 'Progress';
StepComp.displayName = 'Progress.Step';

const getRect = (el?: Element | null) => {
  if (!el) return { top: 0, right: 0, bottom: 0, left: 0 };
  const rect = el.getBoundingClientRect();

  return {
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left
  };
};
